<script setup>

import Loader from "../../Components/Loader.vue";
import {computed, onBeforeMount, onBeforeUnmount, ref, watch} from "vue";
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler";
import _ from 'lodash';
import {useToast} from "vue-toast-notification";
import PaginationCentered from "../../Components/PaginationCentered.vue";
import {useAuthUserStore} from "../../Store/AuthUserStore.js";
import Modal from "../../Components/Modal.vue";
const $toast = useToast();
const { httpErrorsHandler } = useHttpErrorsHandler();

const url = ref('');

watch(url, (newValue) => {
    localStorage.setItem('newsListUrl', url.value);
    localStorage.setItem('newsSearchLoaded', searchLoaded.value.toString());
    localStorage.setItem('newsQuery', query.value);
    localStorage.setItem('newsFilterCategoryId', filterCategoryId.value);
});

onBeforeMount(() => {
    if (localStorage.getItem('newsListUrl')) {
        url.value = localStorage.getItem('newsListUrl');
        searchLoaded.value = localStorage.getItem('newsSearchLoaded')  === 'true';
        query.value = localStorage.getItem('newsQuery');
        filterCategoryId.value = isNaN(localStorage.getItem('newsFilterCategoryId')) ? null : parseInt(localStorage.getItem('newsFilterCategoryId'));
    }else{
        url.value = '/api/news?sortingField=created_at&sortingOrder=desc';
    }
    loadData(url.value);
})

//Get data
const loading = ref(true);
const news = ref([]);
function loadData(urlToFetch) {
    news.value = [];
    loading.value = true;
    axios.get(urlToFetch)
        .then(response => {
            if (searchLoaded.value) {
                return performSearch();
            }
            if(response.data.meta.current_page > response.data.meta.last_page){
                url.value = urlToFetch.replace(/page=\d+/, 'page=' + 1);
                return loadData(url.value);
            }
            paginationAndDataMapping(response.data);
            window.scrollTo(0, 0);
            loading.value = false;
            url.value = urlToFetch;
        })
        .catch(error => {
            httpErrorsHandler(error);
            loading.value = false;
        });
}
//Pagination
const searchLoaded = ref(false);
const savedResponse = ref(null);
const links = ref([]);
const meta = ref([]);
function paginationAndDataMapping(response, mustSave = true) {
    news.value = response.data;
    links.value = response.links;
    meta.value = response.meta;
    if (mustSave === true && searchLoaded.value === false) {
        savedResponse.value = response;
    }
}

//Searching
const query = ref('');
const search = _.debounce(async () => {
    if (query.value.length > 2) {
        await performSearch();
    } else {
        clearSearch();
    }
}, 250);
const performSearch = async () => {
    news.value = [];
    loading.value = true;
    let searchUrl = ''
    if (filterCategoryId.value === null){
        searchUrl  = '/api/search/news?sortingField=created_at&sortingOrder=desc&query=' + query.value;
    }else {
        searchUrl = '/api/search/news?sortingField=created_at&sortingOrder=desc&query=' + query.value + '&category_id=' + filterCategoryId.value;
    }
    axios.get(searchUrl)
        .then(response => {
            paginationAndDataMapping(response.data, false);
            url.value = searchUrl
            searchLoaded.value = true;
            loading.value = false;
        })
        .catch(error => {
            httpErrorsHandler(error);
            loading.value = false;
        });
};
const clearSearch = () => {
    if (savedResponse.value === null) {
        news.value = [];
        url.value = '/api/news?sortingField=created_at&sortingOrder=desc';
        loadData(url.value);
    } else {
        paginationAndDataMapping(savedResponse.value);
    }
    searchLoaded.value = false;
};

const newsCategories = ref([]);
function getNewsCategories(){
    axios.get('/api/categories?category_type=App\\Models\\News&sortingField=name&sortingOrder=asc&all')
        .then(response => {
            newsCategories.value = response.data.data;
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

onBeforeMount(() => {
    getNewsCategories();
})

const modaleDeleteShown = ref(false);
const idToDelete = ref(null);
const indexToDelete = ref(null);
function showModalDelete(id, index) {
    idToDelete.value = id;
    indexToDelete.value = index;
    const modaleDelete = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalDelete'))
    modaleDelete.show()
    modaleDeleteShown.value = true
}
function deleteNews() {
    axios.delete('/api/news/' + idToDelete.value)
        .then(response => {
            loadData(url.value);
            getNewsCategories();
            const modaleDelete = bootstrap.Modal.getInstance(document.getElementById('modalDelete'))
            modaleDelete.hide()
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}
onBeforeUnmount(() => {
    if (modaleDeleteShown.value) {
        const modaleDelete = bootstrap.Modal.getInstance(document.getElementById('modalDelete'))
        modaleDelete.hide()
    }
})

const filterCategoryId = ref(null);
function filterNewsByCategory(categoryId){
    filterCategoryId.value = categoryId;
    if (categoryId === null) {
        url.value = '/api/news?sortingField=created_at&sortingOrder=desc';
        return loadData(url.value);
    }
    url.value = '/api/news?sortingField=created_at&sortingOrder=desc&category_id=' + categoryId;
    loadData(url.value);
}

const authUserStore = useAuthUserStore();
const authUser = computed(() => authUserStore.authUser);

// Réactions d'une actualité, pour le résumé de la carte
const reactionTotal = (newsItem) => (newsItem.data.reactions ?? []).reduce((total, reaction) => total + reaction.count, 0)
const reactionDetail = (newsItem) => (newsItem.data.reactions ?? []).map((reaction) => `${reaction.emoji} ${reaction.count}`).join('  ·  ')
</script>

<template>
    <div class="row">
        <!-- Sidebar: Search and categories -->
        <div class="col-xxl-3 col-12">
            <div class="card">
                <div class="card-header">
                    <p class="text-muted text-uppercase fs-12 fw-medium mb-3">Recherche</p>
                    <div class="search-box search-box-sm">
                        <input type="text" class="form-control bg-light border-0" id="searchNewsList" placeholder="Rechercher..." @input="search" v-model="query">
                        <i class="ri-search-line search-icon"></i>
                    </div>
                </div>
                <div class="accordion accordion-flush filter-accordion">
                    <div class="card-body border-bottom">
                        <div>
                            <div class="d-flex justify-content-between">
                                <p class="text-muted text-uppercase fs-12 fw-medium mb-3">Catégories</p>
                                <a href="#" class="text-danger text-decoration-underline" @click.prevent="filterNewsByCategory(null)" v-if="filterCategoryId !== null">Supprimer filtre</a>
                            </div>
                            <ul class="list-unstyled mb-0 filter-list">
                                <li v-for="(newsCategory, index) in newsCategories" :key="index" @click.prevent="filterNewsByCategory(newsCategory.data.id)">
                                    <a href="#" class="d-flex py-1 align-items-center">
                                        <div class="flex-grow-1">
                                            <h5 class="fs-13 mb-0 listname" :class="{ 'text-secondary': filterCategoryId === newsCategory.data.id }">{{ newsCategory.data.name }}</h5>
                                        </div>
                                        <div class="flex-shrink-0 ms-2">
                                            <span class="badge bg-secondary-subtle text-secondary">{{ newsCategory.data.news_count }}</span>
                                        </div>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="card-body border-bottom" v-if="authUser.can && authUser.can.create_news">
                        <div class="d-grid">
                            <router-link :to="{'name' : 'news.create' }" class="btn btn-secondary waves-effect waves-light" type="button">Ajouter</router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="col-xxl-9 col-12">

            <!-- News -->
            <div class="row">
                <div class="col-xl-4 col-sm-12 pb-4" v-for="(newsItem, index) in news" :key="index">
                    <div class="card h-100">
                        <router-link :to="{ name: 'news.show', params: { id: newsItem.data.id } }">
                            <img class="card-img-top img-fluid" :src="'/format-image/'+newsItem.data.image+'?w=800&h=470&fm=webp&fit=crop'" :alt="'Thumbnail actualité' + newsItem.data.id" width="800" height="470">
                        </router-link>
                        <div class="card-body pb-0">
                            <div class="news-date">{{ newsItem.data.created_at_fr_human }}</div>
                            <router-link :to="{ name: 'news.show', params: { id: newsItem.data.id } }">
                                <h4 class="card-title">{{ newsItem.data.title }}</h4>
                            </router-link>
                            <p class="card-text text-muted">{{ newsItem.data.content_txt.slice(0, 180) }}...</p>
                        </div>
                        <div class="card-footer d-flex justify-content-between align-items-center">
                            <!-- Engagement : réactions (détail au survol) et commentaires -->
                            <router-link :to="{ name: 'news.show', params: { id: newsItem.data.id } }" class="d-flex align-items-center flex-wrap gap-2">
                                <span class="news-engagement" v-if="reactionTotal(newsItem) > 0" v-tooltip="reactionDetail(newsItem)">
                                    <span class="news-engagement-emojis">{{ newsItem.data.reactions.slice(0, 3).map((reaction) => reaction.emoji).join('') }}</span>
                                    <span class="news-engagement-count">{{ reactionTotal(newsItem) }}</span>
                                </span>
                                <span class="news-engagement" v-tooltip="newsItem.data.comments_count > 1 ? `${newsItem.data.comments_count} commentaires` : `${newsItem.data.comments_count ?? 0} commentaire`">
                                    <i class="mdi mdi-comment-outline"></i>
                                    <span class="news-engagement-count">{{ newsItem.data.comments_count ?? 0 }}</span>
                                </span>
                            </router-link>
                            <div>
                                <!--UPDATE-->
                                <router-link :to="{ name: 'news.edit', params:{ id: newsItem.data.id } }" class="text-info" title="Modifier l'actualité" v-if="newsItem.can.update">
                                    <i class="ri-pencil-fill fs-17 px-1"></i>
                                </router-link>
                                <!--Delete-->
                                <a href="#" class="text-danger" title="Supprimer l'actualité"  data-bs-toggle="modal" data-bs-target="#modalDelete" @click.prevent="showModalDelete(newsItem.data.id, index)" v-if="newsItem.can.delete">
                                    <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination or Loader -->
            <div v-if="!loading">
                <pagination-centered
                    :visible="news.length > 0"
                    @change-page:clicked="loadData($event)"
                    :links="links"
                    :meta="meta"
                />
                <div class="text-center" v-if="news.length === 0">
                    <i class="mdi mdi-newspaper-remove fs-36 text-muted"></i>
                    <p class="text-muted">Aucune actualité</p>
                </div>
            </div>

            <div v-if="loading">
                <loader/>
            </div>

            <modal id="modalDelete" title="Supprimer l'actualité ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteNews">
                <template v-slot:icon>
                    <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px" ></lord-icon>
                </template>
                <template v-slot:content>
                    <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de supprimer une actualité.</p>
                </template>
            </modal>
        </div>
    </div>
</template>


<style scoped>


.news-date {
    margin-bottom: 0.35rem;
    font-size: 0.75rem;
    color: var(--vz-secondary-color);
}

/* Pastilles d'engagement : lisibles d'un coup d'œil */
.news-engagement {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    height: 1.7rem;
    padding: 0 0.6rem;
    border-radius: 999px;
    background-color: var(--vz-light);
    color: var(--vz-secondary-color);
    line-height: 1;
    transition: background-color 0.15s ease;
}

.news-engagement:hover {
    background-color: var(--vz-secondary-bg-subtle);
}

/* Icône et emojis de même hauteur que le chiffre, centrés sur la même ligne */
.news-engagement .mdi,
.news-engagement-emojis {
    display: inline-flex;
    align-items: center;
    font-size: 0.9rem;
    line-height: 1;
}

.news-engagement-emojis {
    letter-spacing: -0.1rem;
}

.news-engagement-count {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1;
    color: var(--vz-body-color);
}
</style>
